import * as React from 'react';
import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaExchangeAlt } from 'react-icons/fa';
import { colors, shadows } from '../features/employees/styles/personalakteStyles';
import SwapService, { SwapRequestDetail, SwapRequestMinimal } from '../features/shifts/services/SwapService';

export interface ShiftSwapIndicatorProps {
  shiftId: number;
  swapRequests: SwapRequestMinimal[];
  allEmployees?: { id: number; first_name: string; last_name: string; photo_url?: string }[];
  accentColor?: string;
  assignedEmployees?: { id: number; first_name: string; last_name: string }[];
}

const STATUS_STYLES: Record<string, { color: string; label: string }> = {
  pending:   { color: colors.slate,  label: 'Angefragt' },
  fulfilled: { color: '#15803d',     label: 'Getauscht' },
  rejected:  { color: '#dc2626',     label: 'Abgelehnt' },
  cancelled: { color: colors.textMuted, label: 'Abgebrochen' },
};

function formatShiftTime(start: string, end: string): string {
  const fmt = (t: string) => {
    if (!t) return '--:--';
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(t)) return t.slice(0, 5);
    const m = /T(\d{2}:\d{2})/.exec(t);
    if (m) return m[1];
    return t.slice(0, 5);
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (m) return `${m[3]}.${m[2]}.`;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return formatDate(dateStr);
  const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

export default function ShiftSwapIndicator({ shiftId, swapRequests, allEmployees = [], accentColor = colors.slate, assignedEmployees = [] }: ShiftSwapIndicatorProps): React.ReactElement | null {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [details, setDetails] = useState<SwapRequestDetail[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const relevant = swapRequests.filter((sr: any) => {
    if (sr.shift_id === shiftId) return true;
    if (sr.requester_shift_id === shiftId) return true;
    return sr.options?.some((o: any) => o.target_shift_id === shiftId);
  });

  if (relevant.length === 0) return null;

  const isSwapAway = relevant.some((sr: any) => !sr.is_target && (sr.shift_id === shiftId || sr.requester_shift_id === shiftId));
  const badgeLabel = isSwapAway ? 'SA' : 'SR';

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popoverWidth = 300;
      let top = rect.bottom + 8;
      let left = rect.left;
      if (top + 350 > window.innerHeight - 16) top = rect.top - 350 - 8;
      if (top < 16) top = 16;
      if (left + popoverWidth > window.innerWidth - 16) left = window.innerWidth - popoverWidth - 16;
      if (left < 16) left = 16;
      setPopoverPosition({ top, left });
    }
    setShowPopover(true);
    setLoadingDetails(true);
    try {
      const ids = [...new Set(relevant.map((sr: any) => sr.swap_request_id || sr.id).filter(Boolean))];
      const loaded = await Promise.all(ids.map(id => SwapService.getSwapRequest(id)));
      console.log('[SwapIndicator] detail response:', JSON.stringify(loaded[0], null, 2));
      setDetails(loaded);
    } catch {
      setDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const findEmployee = (empId: number) => allEmployees.find(e => e.id === empId);

  const renderAvatar = (name: string, photoUrl?: string) => {
    const hasPhoto = !!photoUrl;
    return (
      <div style={{
        width: 26, height: 26, borderRadius: hasPhoto ? '50%' : '6px',
        background: hasPhoto ? 'transparent' : colors.slate,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 700, fontSize: '9px', flexShrink: 0,
        border: `2px solid ${colors.border}`, overflow: 'hidden',
      }}>
        {hasPhoto
          ? <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : getInitials(name)
        }
      </div>
    );
  };

  const renderShiftInfo = (shift: { date: string; start: string; end: string; work_area: string } | undefined) => {
    if (!shift) return '';
    return `${shift.work_area} ${formatDate(shift.date)} ${formatShiftTime(shift.start, shift.end)}`;
  };

  return (
    <div ref={containerRef} style={{ position: 'absolute', bottom: 22, right: 5 }}>
      <div
        data-testid="shift-swap-indicator"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: 16, height: 16, borderRadius: '50%',
          background: isHovered ? `${accentColor}18` : 'transparent',
          border: `0.75px solid ${accentColor}`,
          color: accentColor,
          fontSize: 6, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          transform: isHovered ? 'scale(1.2)' : 'scale(1)',
          boxShadow: isHovered ? `0 2px 8px ${accentColor}44` : 'none',
          letterSpacing: '-0.3px',
        }}
        title={`${relevant.length} Tausch${relevant.length > 1 ? 'anfragen' : 'anfrage'} (${isSwapAway ? 'Swap Away' : 'Swap Request'})`}
      >
        {badgeLabel}
      </div>

      {showPopover && createPortal(
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onClick={(e) => { e.stopPropagation(); setShowPopover(false); }}
          />
          <div
            data-testid="shift-swap-popover"
            style={{
              position: 'fixed',
              top: popoverPosition.top,
              left: popoverPosition.left,
              minWidth: 280,
              maxWidth: 340,
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '14px',
              padding: '14px 16px',
              border: `1px solid ${colors.border}`,
              boxShadow: shadows.elevated,
              zIndex: 9999,
              animation: 'slideIn 0.15s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              fontSize: 10, fontWeight: 700, color: colors.slate,
              marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              Schichttausch ({relevant.length})
            </div>

            {loadingDetails ? (
              <div style={{ fontSize: 12, color: colors.textMuted, padding: '8px 0' }}>Laden...</div>
            ) : details.length === 0 ? (
              <div style={{ fontSize: 12, color: colors.textLight, padding: '8px 0', fontStyle: 'italic' }}>Keine Details verfügbar</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {details.map(detail => {
                  const ss = STATUS_STYLES[detail.status] || STATUS_STYLES.pending;
                  const isRequester = detail.requester_shift?.id === shiftId;
                  // Resolve requester name from any relevant entry or detail response
                  const requesterEmp = (detail as any).requester_employee;
                  const reqName = requesterEmp?.name
                    || relevant.map((sr: any) => sr.requester_name).find(Boolean)
                    || (() => {
                      const empId = requesterEmp?.id || relevant.map((sr: any) => sr.employee_id).find(Boolean);
                      if (empId) { const emp = findEmployee(empId); if (emp) return `${emp.first_name} ${emp.last_name}`; }
                      return null;
                    })();
                  const requesterName = reqName;

                  return (
                    <div key={detail.id} style={{
                      padding: '10px 12px', borderRadius: 10,
                      background: colors.bgMuted,
                      border: `1px solid ${colors.border}`,
                    }}>
                      {/* Header: Status + Datum */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: ss.color,
                          padding: '2px 6px', background: 'rgba(255,255,255,0.8)', borderRadius: 4,
                          border: `1px solid ${colors.borderLight}`,
                        }}>
                          {ss.label}
                        </span>
                        <span style={{ fontSize: 9, color: colors.textMuted }}>
                          {formatDateTime(detail.created_at)}
                        </span>
                      </div>

                      {/* Requester info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        {requesterName && renderAvatar(requesterName, findEmployee(requesterEmp?.id || relevant.map((sr: any) => sr.employee_id).find(Boolean))?.photo_url)}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 11, color: colors.textPrimary }}>
                            {requesterName || 'Unbekannt'}
                          </div>
                          <div style={{ fontSize: 9, color: colors.textMuted }}>
                            {isRequester ? 'Diese Schicht' : renderShiftInfo(detail.requester_shift)}
                          </div>
                        </div>
                      </div>

                      {/* Note */}
                      {(detail as any).note && (
                        <div style={{
                          fontSize: 10, color: colors.textBody, fontStyle: 'italic',
                          padding: '4px 8px', background: 'rgba(255,255,255,0.7)', borderRadius: 6,
                          marginBottom: 6, lineHeight: 1.4, border: `1px solid ${colors.borderLight}`,
                        }}>
                          "{(detail as any).note}"
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 6px', color: colors.textMuted }}>
                        <FaExchangeAlt size={9} />
                        <span style={{ fontSize: 10 }}>tauschen mit</span>
                      </div>

                      {/* Options / target shifts */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {detail.options.map(opt => {
                          const emp = findEmployee(opt.target_employee?.id);
                          const empName = opt.target_employee?.name || (emp ? `${emp.first_name} ${emp.last_name}` : `Employee #${opt.target_employee?.id}`);
                          const optSs = STATUS_STYLES[opt.status] || STATUS_STYLES.pending;
                          const isTarget = opt.target_shift?.id === shiftId;
                          return (
                            <div key={opt.option_id} style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 8px', borderRadius: 8,
                              background: 'rgba(255,255,255,0.7)',
                            }}>
                              {renderAvatar(empName, emp?.photo_url)}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 11, color: colors.textPrimary }}>
                                  {empName}
                                </div>
                                <div style={{ fontSize: 9, color: colors.textMuted }}>
                                  {isTarget ? 'Diese Schicht' : renderShiftInfo(opt.target_shift)}
                                </div>
                              </div>
                              <span style={{
                                fontSize: 9, fontWeight: 600, color: optSs.color,
                                padding: '2px 5px', background: 'rgba(255,255,255,0.8)', borderRadius: 4,
                                border: `1px solid ${colors.borderLight}`,
                              }}>
                                {optSs.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Expires info */}
                      {detail.expires_at && (
                        <div style={{ fontSize: 9, color: colors.textMuted, marginTop: 6, textAlign: 'right' }}>
                          Läuft ab: {formatDate(detail.expires_at)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
